import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stats, Badges, type UserStats, type UserBadge, type BadgeDefinition, type CategoryCount } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Skeleton, type IconName } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';

/**
 * Stats page — personal year-in-review meets Untappd profile.
 *
 * Three sections:
 *   1. Top-line numbers (total tastings, rated, average, unique wines)
 *   2. 12-month sparkline + breakdowns (country, region, category, serving)
 *   3. Top bottles + earned badges + locked badges (gallery)
 *
 * Source-of-truth aggregation lives server-side. This page is purely
 * presentational — every metric is keyed off a single React Query.
 */

type Tab = 'me' | 'household';

const TAB_OPTIONS: { id: Tab; label: string; icon: 'user' | 'users' }[] = [
  { id: 'me', label: 'Mine', icon: 'user' },
  { id: 'household', label: 'Husholdning', icon: 'users' },
];

export function StatsPage() {
  const [tab, setTab] = useState<Tab>('me');

  const statsQ = useQuery({
    queryKey: ['stats', tab],
    queryFn: () => (tab === 'me' ? Stats.me() : Stats.household()),
    staleTime: 60_000,
  });

  const badgesQ = useQuery({
    queryKey: ['badges', 'me-all'],
    queryFn: () => Badges.all(),
    enabled: tab === 'me',
    staleTime: 60_000,
  });

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Statistikk</h1>
        <p className="text-sm text-mist">
          Hva du har drukket, vurdert og samlet — og hva du kan låse opp av merker.
        </p>
      </header>

      <div className="flex gap-2">
        {TAB_OPTIONS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`ui-pill ${tab === t.id ? 'ui-pill--active' : ''}`}
          >
            <Icon name={t.icon} size={12} />
            {t.label}
          </button>
        ))}
      </div>

      {statsQ.isLoading || !statsQ.data ? (
        <StatsLoading />
      ) : (
        <StatsBody stats={statsQ.data} />
      )}

      {tab === 'me' && (
        <BadgesSection badgesQ={badgesQ} />
      )}
    </div>
  );
}

function StatsBody({ stats }: { stats: UserStats }) {
  return (
    <div className="grid gap-6">
      {/* Top-line metrics */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricCard label="Smakinger" value={stats.totalCheckIns} />
        <MetricCard label="Vurderte" value={stats.totalRated} />
        <MetricCard
          label="Snitt-vurdering"
          value={stats.averageRating != null ? `${stats.averageRating.toFixed(2)} / 5` : '—'}
        />
        <MetricCard label="Unike viner" value={stats.uniqueWines} />
      </div>

      {stats.totalCheckIns === 0 ? (
        <EmptyState
          icon="check-circle"
          title="Ingen statistikk enda"
          description='Logg en smaking fra en flaske, så bygger vi statistikk her.'
        />
      ) : (
        <>
          {/* 12-month sparkline */}
          <Card className="grid gap-3 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="calendar" className="text-candle" size={16} />
                <h2 className="font-serif text-lg text-parchment">Aktivitet siste 12 måneder</h2>
              </div>
              {stats.avgDaysBetween != null && (
                <span className="text-[10px] uppercase tracking-widest text-mist">
                  ≈ {stats.avgDaysBetween < 1 ? '<1' : stats.avgDaysBetween.toFixed(0)} dag mellom smakinger
                </span>
              )}
            </div>
            <Sparkline buckets={stats.recent12Months} />
          </Card>

          {/* Breakdowns grid */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <BreakdownCard title="Land" icon="globe" entries={stats.byCountry} />
            <BreakdownCard title="Områder" icon="region" entries={stats.byRegion} />
            <BreakdownCard title="Kategori" icon="bottle" entries={stats.byCategory} />
            <BreakdownCard title="Servering" icon="chef-hat" entries={stats.byServingStyle} />
          </div>

          {/* Top bottles */}
          {stats.topBottles.length > 0 && (
            <Card className="grid gap-3 p-5">
              <div className="flex items-center gap-2">
                <Icon name="star" className="text-candle" size={16} />
                <h2 className="font-serif text-lg text-parchment">Mest logget</h2>
              </div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {stats.topBottles.map((b, i) => (
                  <li
                    key={`${b.productId ?? b.bottleId ?? b.name}-${i}`}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded border border-line p-2"
                  >
                    <div className="relative grid h-12 w-9 place-items-center overflow-hidden rounded bg-ink-soft">
                      <BottleImage src={b.imageUrl} iconSize={14} />
                    </div>
                    {b.bottleId || b.productId ? (
                      <Link
                        to={`/bottle/${b.bottleId ?? b.productId}`}
                        className="grid min-w-0 hover:text-candle"
                      >
                        <span className="truncate text-sm text-parchment">{b.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-mist">
                          {b.count}× {b.averageRating != null && `· snitt ${b.averageRating.toFixed(1)}`}
                        </span>
                      </Link>
                    ) : (
                      <div className="grid min-w-0">
                        <span className="truncate text-sm text-parchment">{b.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-mist">
                          {b.count}×{' '}
                          {b.averageRating != null && `· snitt ${b.averageRating.toFixed(1)}`}
                        </span>
                      </div>
                    )}
                    <Badge tone="gold">#{i + 1}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card className="grid gap-1 p-4">
      <span className="text-[10px] uppercase tracking-widest text-mist">{label}</span>
      <span className="font-serif text-2xl text-parchment">{value}</span>
    </Card>
  );
}

function BreakdownCard({
  title,
  icon,
  entries,
}: {
  title: string;
  icon: IconName;
  entries: CategoryCount[];
}) {
  if (entries.length === 0) return null;
  const max = entries.reduce((m, e) => Math.max(m, e.count), 0);
  return (
    <Card className="grid gap-2 p-4">
      <div className="flex items-center gap-2">
        <Icon name={icon} className="text-candle" size={14} />
        <h3 className="font-serif text-sm text-parchment">{title}</h3>
      </div>
      <ul className="grid gap-1.5">
        {entries.slice(0, 6).map((e) => {
          const pct = max > 0 ? (e.count / max) * 100 : 0;
          return (
            <li key={e.key} className="grid gap-0.5">
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="truncate text-parchment">{e.label}</span>
                <span className="text-mist tabular-nums">{e.count}</span>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-ink-soft">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-ruby to-candle"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function Sparkline({ buckets }: { buckets: UserStats['recent12Months'] }) {
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  return (
    <div className="grid grid-cols-12 gap-1 h-20 items-end">
      {buckets.map((b) => {
        const pct = max > 0 ? (b.count / max) * 100 : 0;
        return (
          <div key={b.month} className="grid place-items-end">
            <div
              className="w-full rounded-t bg-gradient-to-t from-ruby/40 to-candle/80"
              style={{ height: `${Math.max(4, pct)}%` }}
              title={`${b.month}: ${b.count} smakinger`}
            />
            <span className="mt-1 text-[9px] uppercase tracking-widest text-mist">
              {b.month.slice(5)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatsLoading() {
  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={70} rounded="md" />
        ))}
      </div>
      <Skeleton height={120} rounded="md" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height={160} rounded="md" />
        ))}
      </div>
    </div>
  );
}

function BadgesSection({
  badgesQ,
}: {
  badgesQ: ReturnType<typeof useQuery<{ earned: UserBadge[]; locked: BadgeDefinition[] }>>;
}) {
  if (badgesQ.isLoading) return <Skeleton height={200} rounded="md" />;
  if (!badgesQ.data) return null;
  const { earned, locked } = badgesQ.data;
  return (
    <div className="grid gap-4">
      <div className="flex items-center gap-2">
        <Icon name="sparkles" className="text-candle" size={18} />
        <h2 className="ui-section-title text-2xl">Merker</h2>
        <Badge tone="gold" className="ml-2">{earned.length} opplåst</Badge>
        <Badge tone="mist">{locked.length} igjen</Badge>
      </div>

      {earned.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {earned.map((b) => (
            <BadgeTile key={b.definition.id} earned={b} />
          ))}
        </div>
      )}

      {locked.length > 0 && (
        <details className="grid gap-2">
          <summary className="cursor-pointer text-sm text-mist hover:text-candle">
            Se {locked.length} merker du ennå ikke har låst opp
          </summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {locked.map((d) => (
              <BadgeTile key={d.id} locked={d} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function BadgeTile({ earned, locked }: { earned?: UserBadge; locked?: BadgeDefinition }) {
  const def = earned?.definition ?? locked;
  if (!def) return null;
  const isEarned = Boolean(earned);
  const tone =
    def.tier === 'gold'
      ? 'gold'
      : def.tier === 'silver'
        ? 'mist'
        : def.tier === 'platinum'
          ? 'gold'
          : 'mist';
  return (
    <Card
      className={`grid gap-2 p-4 ${isEarned ? '' : 'opacity-50'}`}
      glow={isEarned && def.tier === 'gold'}
    >
      <div className="flex items-start gap-3">
        <div
          className={`grid h-12 w-12 shrink-0 place-items-center rounded-full ${
            isEarned ? 'bg-candle/15 text-candle' : 'bg-ink-soft text-mist'
          }`}
        >
          <Icon name={def.icon as IconName} size={22} />
        </div>
        <div className="grid min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-serif text-sm text-parchment line-clamp-1">{def.name}</h3>
            <Badge tone={tone}>{def.tier}</Badge>
          </div>
          <p className="text-xs text-mist line-clamp-2">{def.description}</p>
          {earned?.earnedAt && (
            <span className="mt-1 text-[10px] uppercase tracking-widest text-mist">
              låst opp {new Date(earned.earnedAt).toLocaleDateString('nb-NO')}
              {earned.count != null && ` · ${earned.count}`}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
